/**
 * Phase 8 T-25 §29.B — JD Quote Card.
 *
 * The user's original intake prompt — renders as a right-aligned chat
 * bubble (matching `UserRefinementBubble`) so the clarification thread
 * reads as a proper conversation rather than a "quote card → response"
 * pattern. Post user-feedback (21-Apr): the previous zinc-50 block felt
 * detached from the agentic flow; a chat bubble anchors it firmly as
 * "the user's opening message".
 *
 * The `variant="card"` fallback preserves the old zinc-50 quote look for
 * legacy surfaces (e.g. the Smart Match sandbox page) where the card
 * visual is expected.
 */

interface JdQuoteCardProps {
  /** The user's original input (raw text, preserved newlines). */
  prompt: string;
  /**
   * Visual treatment:
   *  - `bubble` (default) — right-aligned emerald-tinted chat bubble.
   *  - `card` — legacy zinc-50 quote card.
   */
  variant?: 'bubble' | 'card';
}

export function JdQuoteCard({ prompt, variant = 'bubble' }: JdQuoteCardProps) {
  const trimmed = prompt.trim();
  if (!trimmed) return null;

  if (variant === 'card') {
    return (
      <div
        className="rounded-[10px] border px-4 py-3"
        style={{
          background: 'rgb(250 250 250)',
          borderColor: 'rgb(228 228 231)',
          color: 'rgb(39 39 42)',
        }}
        aria-label="Your original job description"
      >
        <p
          className="whitespace-pre-wrap break-words text-[15px] leading-[1.6]"
          style={{ color: 'rgb(39 39 42)' }}
        >
          {trimmed}
        </p>
      </div>
    );
  }

  return (
    <div className="flex justify-end" aria-label="Your original job description">
      <div
        className="max-w-[78%] rounded-[14px] rounded-tr-[4px] border px-3.5 py-2.5 text-[13.5px] leading-[1.55] whitespace-pre-wrap break-words shadow-sm"
        style={{
          borderColor: 'color-mix(in srgb, var(--ggj-accent,#10B981) 35%, var(--vscode-border-primary))',
          background: 'var(--ggj-accent-soft,#D1FAE5)',
          color: 'var(--vscode-text-primary)',
        }}
      >
        {trimmed}
      </div>
    </div>
  );
}
